import { useEffect, useRef } from 'react'
import {
  FaBolt,
  FaChartBar,
  FaChartLine,
  FaClock,
  FaCoins,
  FaFileInvoiceDollar,
  FaGripVertical,
  FaHistory,
  FaInfoCircle,
  FaPiggyBank,
  FaRegStickyNote,
  FaStream,
  FaTasks,
  FaThumbtack,
  FaTimes,
  FaTrophy,
  FaTruck,
  FaUsers,
} from 'react-icons/fa'

/**
 * A mock-up of choosing which widgets appear on your home page. Switching off
 * one of the widgets already on the page hides it for this visit, so the idea
 * can be tried; the rest are ideas only. Nothing is saved.
 */

export interface WidgetOption {
  id: string
  name: string
  description: string
  icon: JSX.Element
  group: string
  /** On this page today, so switching it really does show or hide it. */
  live?: boolean
}

export const WIDGET_OPTIONS: WidgetOption[] = [
  // At a glance
  { id: 'today', name: 'Today at a glance', description: 'Jobs completed, quotes won and invoices raised today.', icon: <FaBolt />, group: 'At a glance', live: true },
  { id: 'tasks', name: 'My tasks', description: 'Your actions, with what is due today and overdue.', icon: <FaTasks />, group: 'At a glance', live: true },
  { id: 'notes', name: 'My open notes', description: 'Notes assigned to you that are still open.', icon: <FaRegStickyNote />, group: 'At a glance' },

  // Sales
  { id: 'pipeline', name: 'Enquiry to job pipeline', description: 'Open enquiries and quotes, and jobs this month.', icon: <FaStream />, group: 'Sales', live: true },
  { id: 'winrate', name: 'Quote win rate', description: 'This month against the year so far.', icon: <FaTrophy />, group: 'Sales', live: true },
  { id: 'trend', name: '12 month trend', description: 'Quotes and jobs by month, against last year.', icon: <FaChartBar />, group: 'Sales', live: true },
  { id: 'followups', name: 'Quote follow-ups due', description: 'Quotes waiting on a follow-up call.', icon: <FaClock />, group: 'Sales' },

  // Finance
  { id: 'overdue', name: 'Overdue invoices', description: 'Unpaid invoices past their due date, oldest first.', icon: <FaFileInvoiceDollar />, group: 'Finance' },
  { id: 'committed', name: 'Committed costs', description: 'What is still to come on open purchase orders.', icon: <FaCoins />, group: 'Finance' },
  { id: 'deliveries', name: 'Awaiting delivery', description: 'Purchase orders not yet fully delivered.', icon: <FaTruck />, group: 'Finance' },
  { id: 'profit', name: 'Forecast profit', description: 'Profit across open jobs, from the pipeline report.', icon: <FaChartLine />, group: 'Finance' },

  // Team
  { id: 'team', name: 'Who is in today', description: 'Who is on site, in the office or off.', icon: <FaUsers />, group: 'Team' },
  { id: 'piggybank', name: 'Piggy bank leaders', description: 'Top referrers this month.', icon: <FaPiggyBank />, group: 'Team' },
  { id: 'activity', name: 'Recent activity', description: 'The latest changes to records you follow.', icon: <FaHistory />, group: 'Team' },
  { id: 'pinned', name: 'Pinned reports', description: 'Shortcuts to the reports you use most.', icon: <FaThumbtack />, group: 'Team' },
]

/** What a new user starts with: everything on the page today. */
export const DEFAULT_WIDGETS = WIDGET_OPTIONS.filter((w) => w.live).map((w) => w.id)

const WidgetPicker = ({
  enabled,
  onToggle,
  onReset,
  onClose,
}: {
  enabled: string[]
  onToggle: (id: string) => void
  onReset: () => void
  onClose: () => void
}) => {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    panelRef.current?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const groups = Array.from(new Set(WIDGET_OPTIONS.map((w) => w.group)))

  return (
    <div className='hp-drawer' role='presentation'>
      <div className='hp-drawer__scrim' onClick={onClose} />
      <div
        ref={panelRef}
        className='hp-drawer__panel'
        role='dialog'
        aria-modal='true'
        aria-labelledby='hp-drawer-title'
        tabIndex={-1}
      >
        <div className='hp-drawer__head'>
          <div>
            <h2 id='hp-drawer-title' className='hp-drawer__title'>
              Customise your home
            </h2>
            <p className='hp-muted' style={{ margin: '4px 0 0' }}>
              {enabled.length} of {WIDGET_OPTIONS.length} widgets on
            </p>
          </div>
          <button type='button' className='hp-drawer__close' onClick={onClose} aria-label='Close'>
            <FaTimes />
          </button>
        </div>

        <div className='hp-drawer__notice'>
          <FaInfoCircle aria-hidden='true' />
          <span>
            Preview only. Switching off a widget that's on the page hides it for this visit; the others are ideas and
            don't appear yet. Nothing is saved.
          </span>
        </div>

        <div className='hp-drawer__body'>
          {groups.map((group) => (
            <section key={group} className='hp-drawer__group'>
              <h3 className='hp-drawer__group-title'>{group}</h3>
              <ul className='hp-widgets'>
                {WIDGET_OPTIONS.filter((w) => w.group === group).map((w) => {
                  const on = enabled.includes(w.id)
                  return (
                    <li key={w.id} className={`hp-widget${on ? ' hp-widget--on' : ''}`}>
                      <span className='hp-widget__grip' aria-hidden='true' title='Drag to reorder (coming later)'>
                        <FaGripVertical />
                      </span>
                      <span className='hp-widget__icon' aria-hidden='true'>
                        {w.icon}
                      </span>
                      <span className='hp-widget__text'>
                        <span className='hp-widget__name'>
                          {w.name}
                          {!w.live && <span className='hp-widget__soon'>Idea</span>}
                        </span>
                        <span className='hp-widget__desc'>{w.description}</span>
                      </span>
                      <button
                        type='button'
                        role='switch'
                        aria-checked={on}
                        aria-label={`${w.name}: ${on ? 'on' : 'off'}`}
                        className={`hp-switch${on ? ' hp-switch--on' : ''}`}
                        onClick={() => onToggle(w.id)}
                      >
                        <span className='hp-switch__knob' />
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>

        <div className='hp-drawer__foot'>
          <button type='button' className='hp-btn hp-btn--ghost' onClick={onReset}>
            Reset to default
          </button>
          <button type='button' className='hp-btn hp-btn--primary' onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

export default WidgetPicker
